import React, { useState } from 'react'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import Checkbox from '@material-ui/core/Checkbox'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import { useTranslation } from 'react-i18next'

import { useCheckFormErrors, useApiCall } from 'hooks'
import { Dialog, UploadButton, snackbar } from 'components'
import { resizeFile } from 'utils'

import { Tag, CreateTagPayload, CreateTagResponse } from '../../types'
import { createTag } from '../../api'

const rules = {
  title: [
    {
      validate: (title: string) => title.length <= 100,
      message: 'No puede superar los 100 caracteres',
    },
  ],
  description: [
    {
      validate: (description: string) => description.length <= 200,
      message: 'No puede superar los 200 caracteres',
    },
  ],
}

interface CreateTagDialogProps {
  handleClose: () => void
  onDone: (newTag: Tag) => void
}

const CreateTagDialog: React.FC<CreateTagDialogProps> = ({ handleClose, onDone }) => {
  const [group, setGroup] = useState('')
  const [subgroup, setSubGroup] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [active, setActive] = useState(false)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState('')
  const [, setStoreImageFile] = useState<File | null>(null)
  const [, setStoreImagePreview] = useState('')

  const [createTagApi, isLoading] = useApiCall<CreateTagPayload, CreateTagResponse>(createTag)
  const { t } = useTranslation()

  const handleTitleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setTitle(e.target.value)
  }

  const handleDescriptionChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setDescription(e.target.value)
  }

  const handleActiveChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setActive(!active)
  }

  const fields = { title, description, group, subgroup }
  const { isAnyFieldEmpty, hasErrors, errors } = useCheckFormErrors(fields, rules)
  const isSubmitDisabled = isAnyFieldEmpty || hasErrors

  const onCreateTag = async () => {
    if (!title || !description) return
    const tag = {
      title,
      description,
      active,
      group,
      subgroup,
      icon: logoFile ?? undefined,
    }
    try {
      const { tag: newTag } = await createTagApi(tag)
      onDone(newTag)
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo crear etiqueta(tag). Intente de nuevo.')
    }
  }
  const handleRemoveImage = (type: 'logo' | 'storeImage') => {
    if (type === 'logo') {
      setLogoFile(null)
      setLogoPreview('')
    } else {
      setStoreImageFile(null)
      setStoreImagePreview('')
    }
  }
  const handleFileChange = async (file: File, type: 'logo' | 'storeImage') => {
    const resizedFile = await resizeFile(file, 192, 192)
    const previewUrl = URL.createObjectURL(resizedFile)
    if (type === 'logo') {
      setLogoFile(resizedFile)
      setLogoPreview(previewUrl)
    } else {
      setStoreImageFile(resizedFile)
      setStoreImagePreview(previewUrl)
    }
  }

  return (
    <Dialog
      title="Nueva etiqueta(tag)"
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isLoading}
      okButtonText="Crear"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={onCreateTag}
    >
      <Grid container spacing={3}>
        <Grid item xs={11}>
          <TextField
            label="Título"
            value={title}
            fullWidth
            onChange={handleTitleChange}
            disabled={isLoading}
            required
            error={errors.title.hasError}
            helperText={errors.title.message}
            inputProps={{ maxLength: 40 }}
          />
        </Grid>
        <Grid item xs={11}>
          <TextField
            label="Descripción"
            value={description}
            fullWidth
            onChange={handleDescriptionChange}
            disabled={isLoading}
            required
            error={errors.description.hasError}
            helperText={errors.description.message}
            inputProps={{ maxLength: 60 }}
          />
        </Grid>
        <Grid item xs={11}>
          <FormControl fullWidth required error={errors.type?.hasError}>
            <InputLabel id="type-select-label">Tipo</InputLabel>
            <Select
              labelId="type-select-label"
              value={group}
              onChange={e => setGroup(e.target.value as string)}
              disabled={isLoading}
            >
              <MenuItem value="ALL">{t('tags:ALL')}</MenuItem>
              <MenuItem value="BRANDS">{t('tags:BRANDS')}</MenuItem>
              <MenuItem value="LEVELS">{t('tags:LEVELS')}</MenuItem>
              <MenuItem value="FILTER">{t('tags:FILTER')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {group === 'FILTER' && (
          <Grid item xs={11}>
            <FormControl fullWidth required error={errors.type?.hasError}>
              <InputLabel id="subtype-select-label">Subtipo</InputLabel>
              <Select
                labelId="subtype-select-label"
                value={subgroup}
                onChange={e => setSubGroup(e.target.value as string)}
                disabled={isLoading}
              >
                <MenuItem value="CATEGORY">{t('tags:CATEGORY')}</MenuItem>
                <MenuItem value="DISCOUNT">{t('tags:DISCOUNT')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography>Logo</Typography>
          <UploadButton
            key={logoPreview || 'empty-logo'} // Forzará el re-render
            id="logo-upload0"
            accept=".jpg, .jpeg, .png"
            label="Subir Logo"
            onChange={file => handleFileChange(file, 'logo')}
          />

          {logoPreview && (
            <>
              <img
                src={logoPreview}
                alt="Logo Preview"
                style={{ width: 100, height: 100, display: 'block', marginTop: 10 }}
              />
              <Button color="secondary" onClick={() => handleRemoveImage('logo')}>
                Eliminar
              </Button>
            </>
          )}
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={<Checkbox onChange={handleActiveChange} checked={active} />}
            label="Activo"
          />
        </Grid>
      </Grid>
    </Dialog>
  )
}

export { CreateTagDialog }
